import React from 'react';
import { ButtonBase, ButtonBaseProps } from '../../ButtonBase';
import { STATIC_STYLES, useDynamicStyles } from './CarouselButton.styles';
import { SvgIconProps } from '../../SvgIcon';
import { Styler, StylerProps } from '../../Styler';
import { ResponsiveStyleValue } from '../../../theming';
import { CarouselButtonSize } from '../Carousel.types';

export interface CarouselButtonProps extends StylerProps, ButtonBaseProps {
    /**
     * If `true`, the button will be hidden for touch devices where users can use swipe gestures.
     * @default false
     */
    hiddenForTouch?: boolean;

    /**
     * The required colour option.
     * @default light
     */
    color?: 'light' | 'dark';

    /**
     * If `true`, the button will be have a border.
     * @default false
     */
    withBorder?: false;

    /**
     * An svg icon component.
     */
    icon: React.ReactElement<SvgIconProps>;

    /**
     * The size of the button.
     * Use object syntax to provide responsive values. The value will apply to the given breakpoint and up, unless overridden.
     * @default m
     */
    size?: ResponsiveStyleValue<CarouselButtonSize>;
}

export function CarouselButton(props: Readonly<CarouselButtonProps>): React.JSX.Element | null {
    const { hiddenForTouch = false, color = 'light', withBorder = false, icon, size = 'm', ref, ...other } = props;

    const cssVariables = useDynamicStyles(color, size);

    return (
        <Styler
            ref={ref}
            component={ButtonBase}
            style={cssVariables}
            baseStyles={STATIC_STYLES}
            data-hidden-for-touch={hiddenForTouch || undefined}
            data-border={withBorder || undefined}
            {...other}
        >
            {icon}
        </Styler>
    );
}
